// EscortLiveTracking.jsx
// Dual map: OpenStreetMap (Leaflet) + Google Maps
// Provider from company user_map (same as RouteMaster / AdminRouteTracking)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Layout from "../layouts/Layout";
import { API_BASE_URL, FONT_UI, SKIN_THEMES } from "../layouts/constants";

/* =========================================================
   GOOGLE MAPS LOADER
========================================================= */

const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;
  return document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");
  if (errorText.includes("RefererNotAllowedMapError")) {
    return "This website is not authorized for the Google Maps API key. Add this domain under the API key website restrictions.";
  }
  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }
  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }
  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }
  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }
  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Google Maps can only be loaded in a browser.")
    );
  }
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!apiKey) {
    return Promise.reject(
      new Error(
        "Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file."
      )
    );
  }
  if (googleMapsLoadingPromise) return googleMapsLoadingPromise;

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;
    const resolveOnce = () => {
      if (settled || !window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };
    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;
      reject(
        error instanceof Error
          ? error
          : new Error("Google Maps JavaScript API failed to load.")
      );
    };
    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }
      rejectOnce(
        new Error("Google Maps script loaded, but the Maps API is unavailable.")
      );
    };
    const handleScriptError = () => {
      rejectOnce(new Error("Google Maps JavaScript API network request failed."));
    };
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (e) {
          console.error(e);
        }
      }
      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };
    const existingScript = findExistingGoogleMapsScript();
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      existingScript.addEventListener("error", handleScriptError, {
        once: true,
      });
      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);
      return;
    }
    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places,geometry",
      v: "weekly",
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${queryParameters.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");
    script.onload = handleScriptLoad;
    script.onerror = () => {
      script.remove();
      handleScriptError();
    };
    document.head.appendChild(script);
  });
  return googleMapsLoadingPromise;
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();
  if (
    provider === "google" ||
    provider === "googlemaps" ||
    provider === "google_maps"
  ) {
    return "google";
  }
  return "openstreetmap";
}

function googleCircleSymbol(color, scale = 10) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
  };
}

/* =========================================================
   SHARED HELPERS
========================================================= */

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtKm(m) {
  return (Number(m || 0) / 1000).toFixed(1);
}

function fmtKph(mps) {
  return Math.round(Number(mps || 0) * 3.6);
}

function parseUtcToIST(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const utcStr = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  const d = new Date(utcStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const clean = s.replace("T", " ").replace("Z", "").split(".")[0];
  const [datePart, timePart] = clean.split(" ");
  if (!datePart || !timePart) return null;
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second = 0] = timePart.split(":").map(Number);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }
  const d = new Date(year, month - 1, day, hour, minute, second);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateObj(d) {
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
    .format(d)
    .replace(",", "");
}

function formatUtcAsIST(ts) {
  return formatDateObj(parseUtcToIST(ts));
}

function humanDuration(startRaw, endRaw) {
  const startD = parseDbIst(startRaw);
  const endD = endRaw ? parseUtcToIST(endRaw) : new Date();
  if (!startD || !endD) return "—";
  const ms = endD.getTime() - startD.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
}

const ESCORT_POLL_MS = 2000;
const MAX_ACCURACY_M = 50;
const MAX_JUMP_M = 120;

function pointKey(p) {
  return String(p?.id ?? `${p?.lat?.toFixed(6)},${p?.lng?.toFixed(6)}`);
}

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (v) => (Number(v) * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function cleanEscortTrack(points = []) {
  const ordered = points
    .map((p) => ({
      ...p,
      lat: toNum(p.lat),
      lng: toNum(p.lng),
      accuracy_m: toNum(p.accuracy_m),
      speed_mps: toNum(p.speed_mps),
      _key: pointKey(p),
    }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .filter((p) => p.accuracy_m === null || p.accuracy_m <= MAX_ACCURACY_M);

  const cleaned = [];
  for (const p of ordered) {
    if (!cleaned.length) {
      cleaned.push(p);
      continue;
    }
    const prev = cleaned[cleaned.length - 1];
    const dist = haversineMeters(prev, p);
    if (dist > MAX_JUMP_M) {
      console.warn("GPS spike skipped:", prev.id, "->", p.id, dist.toFixed(1));
      continue;
    }
    cleaned.push(p);
  }
  return cleaned;
}

/* Leaflet icons */

function vehicleSvg(color = "#111827", size = 54) {
  return `
    <svg viewBox="0 0 64 32" width="${size}" height="${size}" fill="${color}" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="10" width="30" height="12" rx="2"></rect>
      <path d="M34 10h12l6 6v6H34z"></path>
      <circle cx="14" cy="26" r="4" fill="#1f2937"></circle>
      <circle cx="42" cy="26" r="4" fill="#1f2937"></circle>
    </svg>
  `;
}

function vehicleIcon(color = "#111827", size = 54) {
  return L.divIcon({
    className: "escort-user-icon",
    html: vehicleSvg(color, size),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function pinIcon({ color = "#2563eb", label = "●", size = 34 }) {
  return L.divIcon({
    className: "escort-pin-icon",
    html: `
      <div style="width:${size}px;height:${size}px;border-radius:999px;background:${color};color:white;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.35);">
        ${label}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function checkpointIcon(index) {
  const size = 32;
  const label = String(index + 1);
  return L.divIcon({
    className: "escort-checkpoint-icon",
    html: `
      <div style="width:${size}px;height:${size}px;border-radius:50%;background:#f97316;color:white;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.30);font-family:system-ui,sans-serif;">
        ${label}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const fromIcon = () => pinIcon({ color: "#16a34a", label: "F" });
const toIcon = () => pinIcon({ color: "#dc2626", label: "T" });
const stopIcon = () => pinIcon({ color: "#7c3aed", label: "S" });

/* =========================================================
   COMPONENT
========================================================= */

export default function EscortLiveTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const memberId = params.get("member_id") || "";
  const runId = params.get("run_id") || "";
  const limit = params.get("limit") || "2000";

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");
  const [pageError, setPageError] = useState("");

  const isGoogleMap = useMemo(
    () => normalizeMapProvider(dashboardData.user_map) === "google",
    [dashboardData.user_map]
  );

  /* Leaflet refs */
  const leafletMapRef = useRef(null);
  const leafletMapBoxRef = useRef(null);
  const leafletLatestMarkerRef = useRef(null);
  const leafletFromMarkerRef = useRef(null);
  const leafletToMarkerRef = useRef(null);
  const leafletStopMarkerRef = useRef(null);
  const leafletCheckpointMarkersRef = useRef([]);
  const leafletTrackLineRef = useRef(null);

  /* Google refs */
  const googleMapRef = useRef(null);
  const googleMapBoxRef = useRef(null);
  const googleLatestMarkerRef = useRef(null);
  const googleFromMarkerRef = useRef(null);
  const googleToMarkerRef = useRef(null);
  const googleStopMarkerRef = useRef(null);
  const googleCheckpointMarkersRef = useRef([]);
  const googleTrackLineRef = useRef(null);

  const renderedLatLngsRef = useRef([]);
  const renderedKeysRef = useRef(new Set());
  const latestLatLngRef = useRef(null);
  const livePointQueueRef = useRef([]);
  const liveAnimatingRef = useRef(false);
  const lastAnimatedKeyRef = useRef(null);
  const fittedOnceRef = useRef(false);
  const pollRef = useRef(null);
  const stableFromRef = useRef(null);
  const stableToRef = useRef(null);
  const checkpointsRef = useRef([]);
  const checkpointsFetchedRef = useRef(false);

  const [followLive, setFollowLive] = useState(true);
  const followLiveRef = useRef(true);

  const [status, setStatus] = useState({
    label: "—",
    className: "bg-slate-200 text-slate-800",
  });

  const [meta, setMeta] = useState({
    title: "—",
    from: "—",
    to: "—",
    vehicle: "—",
    lastTs: "—",
    distanceKm: "0.0",
    speedKph: "0",
    duration: "—",
    pointsCount: "0",
  });

  const [checkpointCount, setCheckpointCount] = useState(0);

  const routePage = useMemo(
    () => (isGoogleMap ? "/route-master" : "/route-master1"),
    [isGoogleMap]
  );

  const apiUrl = useMemo(() => {
    const q = new URLSearchParams({
      member_id: memberId,
      limit,
      include_segments: "1",
    });
    if (runId) q.set("run_id", runId);
    return api(`/api/escort/live/tracking?${q.toString()}`);
  }, [memberId, runId, limit]);

  const lockStablePins = useCallback((run) => {
    if (!run) return;
    if (!stableFromRef.current) {
      const lat = toNum(run.start_lat ?? run.from_lat);
      const lng = toNum(run.start_lng ?? run.from_lng);
      if (lat !== null && lng !== null) {
        stableFromRef.current = {
          lat,
          lng,
          name: run.from_name || "From",
        };
      }
    }
    if (!stableToRef.current) {
      const lat = toNum(run.end_lat ?? run.to_lat);
      const lng = toNum(run.end_lng ?? run.to_lng);
      if (lat !== null && lng !== null) {
        stableToRef.current = {
          lat,
          lng,
          name: run.to_name || "To",
        };
      }
    }
  }, []);

  const fetchCheckpoints = useCallback(async (routeIdForCheckpoints) => {
    if (!routeIdForCheckpoints) return [];
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(
        api(
          `/api/checkpoints?route_id=${encodeURIComponent(routeIdForCheckpoints)}`
        ),
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        }
      );
      if (!res.ok) return [];
      const data = await res.json();
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      return rows.filter((cp) => {
        const lat = toNum(cp.lat ?? cp.latitude);
        const lng = toNum(cp.lng ?? cp.longitude);
        return lat !== null && lng !== null;
      });
    } catch (err) {
      console.warn("Checkpoint fetch skipped:", err.message);
      return [];
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(api("/api/dashboard/admin/dashboard"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch dashboard");
      const json = await res.json();
      const company = json?.data?.companyDetails || {};
      setDashboardData((prev) => ({
        ...prev,
        ...company,
        user_map: normalizeMapProvider(
          company.user_map ||
            localStorage.getItem("user_map") ||
            prev.user_map
        ),
      }));
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error(error.message || "Failed to load company details.");
      setPageError(error.message || "Failed to load company details.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /* ---------- clear layers ---------- */

  const clearLeafletLayers = useCallback(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    [
      leafletLatestMarkerRef,
      leafletFromMarkerRef,
      leafletToMarkerRef,
      leafletStopMarkerRef,
    ].forEach((ref) => {
      if (ref.current) {
        map.removeLayer(ref.current);
        ref.current = null;
      }
    });
    leafletCheckpointMarkersRef.current.forEach((m) => map.removeLayer(m));
    leafletCheckpointMarkersRef.current = [];
    if (leafletTrackLineRef.current) {
      map.removeLayer(leafletTrackLineRef.current);
      leafletTrackLineRef.current = null;
    }
    renderedLatLngsRef.current = [];
    renderedKeysRef.current = new Set();
    livePointQueueRef.current = [];
    liveAnimatingRef.current = false;
    lastAnimatedKeyRef.current = null;
    latestLatLngRef.current = null;
  }, []);

  const clearGoogleLayers = useCallback(() => {
    [
      googleLatestMarkerRef,
      googleFromMarkerRef,
      googleToMarkerRef,
      googleStopMarkerRef,
    ].forEach((ref) => {
      if (ref.current) {
        ref.current.setMap(null);
        ref.current = null;
      }
    });
    googleCheckpointMarkersRef.current.forEach((m) => m?.setMap(null));
    googleCheckpointMarkersRef.current = [];
    if (googleTrackLineRef.current) {
      googleTrackLineRef.current.setMap(null);
      googleTrackLineRef.current = null;
    }
    renderedLatLngsRef.current = [];
    renderedKeysRef.current = new Set();
    livePointQueueRef.current = [];
    liveAnimatingRef.current = false;
    lastAnimatedKeyRef.current = null;
    latestLatLngRef.current = null;
  }, []);

  const setRunStatus = useCallback((runStatus) => {
    const s = String(runStatus || "").toLowerCase();
    if (s === "active") {
      setStatus({ label: "LIVE", className: "bg-emerald-500 text-white" });
    } else if (["stopped", "abandoned", "completed"].includes(s)) {
      setStatus({
        label: s.toUpperCase(),
        className: "bg-red-100 text-red-700",
      });
    } else {
      setStatus({ label: "—", className: "bg-slate-200 text-slate-800" });
    }
  }, []);

  /* ---------- LEAFLET draw helpers ---------- */

  const drawLeafletCheckpoints = useCallback((map) => {
    leafletCheckpointMarkersRef.current.forEach((m) => map.removeLayer(m));
    leafletCheckpointMarkersRef.current = [];
    const checkpoints = checkpointsRef.current;
    if (!checkpoints?.length) return;
    checkpoints.forEach((cp, index) => {
      const lat = toNum(cp.lat ?? cp.latitude);
      const lng = toNum(cp.lng ?? cp.longitude);
      if (lat === null || lng === null) return;
      const name =
        cp.checkpoint_name || cp.name || `Checkpoint ${index + 1}`;
      const order = cp.checkpoint_order ?? cp.seq_no ?? index + 1;
      const marker = L.marker([lat, lng], {
        icon: checkpointIcon(index),
        zIndexOffset: 800,
      })
        .addTo(map)
        .bindTooltip(
          `<div style="font-family:system-ui,sans-serif;min-width:120px;">
            <div style="font-weight:700;font-size:13px;color:#ea580c;">Checkpoint ${order}</div>
            <div style="font-size:12px;color:#374151;margin-top:2px;">${name}</div>
          </div>`,
          { direction: "top" }
        );
      leafletCheckpointMarkersRef.current.push(marker);
    });
  }, []);

  const placeLeafletEndpoints = useCallback((map, bounds) => {
    const from = stableFromRef.current;
    const to = stableToRef.current;
    if (from) {
      if (leafletFromMarkerRef.current)
        map.removeLayer(leafletFromMarkerRef.current);
      leafletFromMarkerRef.current = L.marker([from.lat, from.lng], {
        icon: fromIcon(),
        zIndexOffset: 900,
      })
        .addTo(map)
        .bindTooltip(`From<br>${from.name}`, { direction: "top" });
      bounds.extend([from.lat, from.lng]);
    }
    if (to) {
      if (leafletToMarkerRef.current)
        map.removeLayer(leafletToMarkerRef.current);
      leafletToMarkerRef.current = L.marker([to.lat, to.lng], {
        icon: toIcon(),
        zIndexOffset: 900,
      })
        .addTo(map)
        .bindTooltip(`To<br>${to.name}`, { direction: "top" });
      bounds.extend([to.lat, to.lng]);
    }
  }, []);

  const placeLeafletStop = useCallback((map, bounds, lat, lng, label) => {
    if (lat === null || lng === null) return;
    if (leafletStopMarkerRef.current)
      map.removeLayer(leafletStopMarkerRef.current);
    leafletStopMarkerRef.current = L.marker([lat, lng], {
      icon: stopIcon(),
      zIndexOffset: 1000,
    })
      .addTo(map)
      .bindTooltip(`Stopped Here<br>${label || ""}`, { direction: "top" });
    bounds.extend([lat, lng]);
  }, []);

  const animateLeafletMarkerTo = useCallback((marker, from, to, duration = 900) => {
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const lat = from.lat + (to.lat - from.lat) * t;
      const lng = from.lng + (to.lng - from.lng) * t;
      marker.setLatLng([lat, lng]);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        latestLatLngRef.current = [to.lat, to.lng];
        liveAnimatingRef.current = false;
        lastAnimatedKeyRef.current = to._key;
        playLiveQueueRef.current();
      }
    };
    requestAnimationFrame(step);
  }, []);

  const playLiveQueueRef = useRef(() => {});

  const playLeafletLiveQueue = useCallback(() => {
    const map = leafletMapRef.current;
    const marker = leafletLatestMarkerRef.current;
    if (!map || !marker || liveAnimatingRef.current) return;
    const nextPoint = livePointQueueRef.current.shift();
    if (!nextPoint) return;
    const current = marker.getLatLng();
    liveAnimatingRef.current = true;
    animateLeafletMarkerTo(
      marker,
      { lat: current.lat, lng: current.lng },
      nextPoint,
      1000
    );
    if (followLiveRef.current) {
      map.panTo([nextPoint.lat, nextPoint.lng], {
        animate: true,
        duration: 0.8,
      });
    }
  }, [animateLeafletMarkerTo]);

  const animateGoogleMarkerTo = useCallback((marker, from, to, duration = 900) => {
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const lat = from.lat + (to.lat - from.lat) * t;
      const lng = from.lng + (to.lng - from.lng) * t;
      marker.setPosition({ lat, lng });
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        latestLatLngRef.current = [to.lat, to.lng];
        liveAnimatingRef.current = false;
        lastAnimatedKeyRef.current = to._key;
        playLiveQueueRef.current();
      }
    };
    requestAnimationFrame(step);
  }, []);

  const playGoogleLiveQueue = useCallback(() => {
    const map = googleMapRef.current;
    const marker = googleLatestMarkerRef.current;
    if (!map || !marker || liveAnimatingRef.current) return;
    const nextPoint = livePointQueueRef.current.shift();
    if (!nextPoint) return;
    const current = marker.getPosition();
    if (!current) return;
    liveAnimatingRef.current = true;
    animateGoogleMarkerTo(
      marker,
      { lat: current.lat(), lng: current.lng() },
      nextPoint,
      1000
    );
    if (followLiveRef.current) {
      map.panTo({ lat: nextPoint.lat, lng: nextPoint.lng });
    }
  }, [animateGoogleMarkerTo]);

  useEffect(() => {
    playLiveQueueRef.current = isGoogleMap
      ? playGoogleLiveQueue
      : playLeafletLiveQueue;
  }, [isGoogleMap, playGoogleLiveQueue, playLeafletLiveQueue]);

  const drawLeafletPlanned = useCallback(
    (run) => {
      const map = leafletMapRef.current;
      if (!map || !run) return;
      clearLeafletLayers();
      lockStablePins(run);
      const bounds = L.latLngBounds();
      placeLeafletEndpoints(map, bounds);
      drawLeafletCheckpoints(map);
      if (bounds.isValid()) map.fitBounds(bounds.pad(0.2));
      else map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
    },
    [
      clearLeafletLayers,
      lockStablePins,
      placeLeafletEndpoints,
      drawLeafletCheckpoints,
    ]
  );

  const drawLeafletTrack = useCallback(
    (data, runStatus) => {
      const map = leafletMapRef.current;
      if (!map) return;
      const payload = data || {};
      const run = payload.run || {};
      const points = cleanEscortTrack(
        Array.isArray(payload?.points) ? payload.points : []
      );
      const bounds = L.latLngBounds();
      lockStablePins(run);
      const isStopped = ["stopped", "abandoned", "completed"].includes(
        String(runStatus || "").toLowerCase()
      );
      placeLeafletEndpoints(map, bounds);
      drawLeafletCheckpoints(map);

      if (!leafletTrackLineRef.current) {
        leafletTrackLineRef.current = L.polyline([], {
          color: "#0ea5e9",
          weight: 6,
          opacity: 0.95,
          lineJoin: "round",
          lineCap: "round",
        }).addTo(map);
      }

      renderedLatLngsRef.current = [];
      renderedKeysRef.current = new Set();
      points.forEach((p) => {
        if (!renderedKeysRef.current.has(p._key)) {
          renderedKeysRef.current.add(p._key);
          renderedLatLngsRef.current.push([p.lat, p.lng]);
          bounds.extend([p.lat, p.lng]);
        }
      });
      leafletTrackLineRef.current.setLatLngs(renderedLatLngsRef.current);

      const latestPoint = points[points.length - 1];
      if (latestPoint && !isStopped) {
        if (!leafletLatestMarkerRef.current) {
          const firstPoint = points[0];
          leafletLatestMarkerRef.current = L.marker(
            [firstPoint.lat, firstPoint.lng],
            {
              icon: vehicleIcon("#111827", 54),
              zIndexOffset: 1200,
            }
          )
            .bindTooltip("Live", { direction: "top" })
            .addTo(map);
          latestLatLngRef.current = [firstPoint.lat, firstPoint.lng];
          lastAnimatedKeyRef.current = firstPoint._key;
          livePointQueueRef.current = points.slice(1);
          playLeafletLiveQueue();
        } else {
          const currentIndex = points.findIndex(
            (p) => p._key === lastAnimatedKeyRef.current
          );
          const newPoints =
            currentIndex >= 0 ? points.slice(currentIndex + 1) : [latestPoint];
          if (newPoints.length > 0) {
            const existingKeys = new Set(
              livePointQueueRef.current.map((p) => p._key)
            );
            newPoints.forEach((p) => {
              if (!existingKeys.has(p._key)) livePointQueueRef.current.push(p);
            });
            playLeafletLiveQueue();
          }
        }
      }

      if (isStopped && latestPoint) {
        if (leafletLatestMarkerRef.current) {
          map.removeLayer(leafletLatestMarkerRef.current);
          leafletLatestMarkerRef.current = null;
        }
        livePointQueueRef.current = [];
        liveAnimatingRef.current = false;
        placeLeafletStop(
          map,
          bounds,
          latestPoint.lat,
          latestPoint.lng,
          formatUtcAsIST(latestPoint.ts || latestPoint.recorded_at)
        );
      }

      if (bounds.isValid() && !fittedOnceRef.current) {
        map.fitBounds(bounds.pad(0.18));
        fittedOnceRef.current = true;
      }
    },
    [
      lockStablePins,
      placeLeafletEndpoints,
      drawLeafletCheckpoints,
      placeLeafletStop,
      playLeafletLiveQueue,
    ]
  );

  /* ---------- GOOGLE draw helpers ---------- */

  const drawGoogleCheckpoints = useCallback((map) => {
    googleCheckpointMarkersRef.current.forEach((m) => m?.setMap(null));
    googleCheckpointMarkersRef.current = [];
    const checkpoints = checkpointsRef.current;
    if (!checkpoints?.length || !window.google?.maps) return;
    checkpoints.forEach((cp, index) => {
      const lat = toNum(cp.lat ?? cp.latitude);
      const lng = toNum(cp.lng ?? cp.longitude);
      if (lat === null || lng === null) return;
      const name =
        cp.checkpoint_name || cp.name || `Checkpoint ${index + 1}`;
      const m = new window.google.maps.Marker({
        map,
        position: { lat, lng },
        title: name,
        label: {
          text: String(index + 1),
          color: "#ffffff",
          fontSize: "11px",
          fontWeight: "700",
        },
        icon: googleCircleSymbol("#f97316", 10),
        zIndex: 800,
      });
      googleCheckpointMarkersRef.current.push(m);
    });
  }, []);

  const placeGoogleEndpoints = useCallback((map, bounds) => {
    if (!window.google?.maps) return;
    const from = stableFromRef.current;
    const to = stableToRef.current;
    if (from) {
      if (googleFromMarkerRef.current) googleFromMarkerRef.current.setMap(null);
      googleFromMarkerRef.current = new window.google.maps.Marker({
        map,
        position: { lat: from.lat, lng: from.lng },
        title: `From: ${from.name}`,
        label: {
          text: "F",
          color: "#ffffff",
          fontSize: "12px",
          fontWeight: "700",
        },
        icon: googleCircleSymbol("#16a34a", 12),
        zIndex: 900,
      });
      bounds.extend({ lat: from.lat, lng: from.lng });
    }
    if (to) {
      if (googleToMarkerRef.current) googleToMarkerRef.current.setMap(null);
      googleToMarkerRef.current = new window.google.maps.Marker({
        map,
        position: { lat: to.lat, lng: to.lng },
        title: `To: ${to.name}`,
        label: {
          text: "T",
          color: "#ffffff",
          fontSize: "12px",
          fontWeight: "700",
        },
        icon: googleCircleSymbol("#dc2626", 12),
        zIndex: 900,
      });
      bounds.extend({ lat: to.lat, lng: to.lng });
    }
  }, []);

  const placeGoogleStop = useCallback((map, bounds, lat, lng, label) => {
    if (lat === null || lng === null || !window.google?.maps) return;
    if (googleStopMarkerRef.current) googleStopMarkerRef.current.setMap(null);
    googleStopMarkerRef.current = new window.google.maps.Marker({
      map,
      position: { lat, lng },
      title: `Stopped: ${label || ""}`,
      label: {
        text: "S",
        color: "#ffffff",
        fontSize: "12px",
        fontWeight: "700",
      },
      icon: googleCircleSymbol("#7c3aed", 12),
      zIndex: 1000,
    });
    bounds.extend({ lat, lng });
  }, []);

  const drawGooglePlanned = useCallback(
    (run) => {
      const map = googleMapRef.current;
      if (!map || !run || !window.google?.maps) return;
      clearGoogleLayers();
      lockStablePins(run);
      const bounds = new window.google.maps.LatLngBounds();
      placeGoogleEndpoints(map, bounds);
      drawGoogleCheckpoints(map);
      if (!bounds.isEmpty()) map.fitBounds(bounds, 48);
      else map.setCenter(DEFAULT_CENTER);
    },
    [
      clearGoogleLayers,
      lockStablePins,
      placeGoogleEndpoints,
      drawGoogleCheckpoints,
    ]
  );

  const drawGoogleTrack = useCallback(
    (data, runStatus) => {
      const map = googleMapRef.current;
      if (!map || !window.google?.maps) return;
      const payload = data || {};
      const run = payload.run || {};
      const points = cleanEscortTrack(
        Array.isArray(payload?.points) ? payload.points : []
      );
      const bounds = new window.google.maps.LatLngBounds();
      lockStablePins(run);
      const isStopped = ["stopped", "abandoned", "completed"].includes(
        String(runStatus || "").toLowerCase()
      );
      placeGoogleEndpoints(map, bounds);
      drawGoogleCheckpoints(map);

      const path = [];
      renderedLatLngsRef.current = [];
      renderedKeysRef.current = new Set();
      points.forEach((p) => {
        if (!renderedKeysRef.current.has(p._key)) {
          renderedKeysRef.current.add(p._key);
          path.push({ lat: p.lat, lng: p.lng });
          renderedLatLngsRef.current.push([p.lat, p.lng]);
          bounds.extend({ lat: p.lat, lng: p.lng });
        }
      });

      if (!googleTrackLineRef.current) {
        googleTrackLineRef.current = new window.google.maps.Polyline({
          map,
          path: [],
          strokeColor: "#0ea5e9",
          strokeOpacity: 0.95,
          strokeWeight: 6,
        });
      }
      googleTrackLineRef.current.setPath(path);

      const latestPoint = points[points.length - 1];
      if (latestPoint && !isStopped) {
        if (!googleLatestMarkerRef.current) {
          const firstPoint = points[0];
          googleLatestMarkerRef.current = new window.google.maps.Marker({
            map,
            position: { lat: firstPoint.lat, lng: firstPoint.lng },
            title: "Live vehicle",
            icon: googleCircleSymbol("#111827", 12),
            zIndex: 1200,
          });
          latestLatLngRef.current = [firstPoint.lat, firstPoint.lng];
          lastAnimatedKeyRef.current = firstPoint._key;
          livePointQueueRef.current = points.slice(1);
          playGoogleLiveQueue();
        } else {
          const currentIndex = points.findIndex(
            (p) => p._key === lastAnimatedKeyRef.current
          );
          const newPoints =
            currentIndex >= 0 ? points.slice(currentIndex + 1) : [latestPoint];
          if (newPoints.length > 0) {
            const existingKeys = new Set(
              livePointQueueRef.current.map((p) => p._key)
            );
            newPoints.forEach((p) => {
              if (!existingKeys.has(p._key)) livePointQueueRef.current.push(p);
            });
            playGoogleLiveQueue();
          }
        }
      }

      if (isStopped && latestPoint) {
        if (googleLatestMarkerRef.current) {
          googleLatestMarkerRef.current.setMap(null);
          googleLatestMarkerRef.current = null;
        }
        livePointQueueRef.current = [];
        liveAnimatingRef.current = false;
        placeGoogleStop(
          map,
          bounds,
          latestPoint.lat,
          latestPoint.lng,
          formatUtcAsIST(latestPoint.ts || latestPoint.recorded_at)
        );
      }

      if (!bounds.isEmpty() && !fittedOnceRef.current) {
        map.fitBounds(bounds, 48);
        fittedOnceRef.current = true;
      }
    },
    [
      lockStablePins,
      placeGoogleEndpoints,
      drawGoogleCheckpoints,
      placeGoogleStop,
      playGoogleLiveQueue,
    ]
  );

  /* ---------- refresh / poll ---------- */

  const refresh = useCallback(async () => {
    if (!memberId) {
      setStatus({
        label: "member_id required",
        className: "bg-red-100 text-red-700",
      });
      toast.error("Member ID is required for tracking");
      return;
    }
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      const res = await fetch(apiUrl, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || data?.ok === false || data?.success === false) {
        throw new Error(data?.error || data?.message || "API error");
      }
      const payload = data?.data || data;
      const run = payload?.run || {};
      const latest = payload?.latest || {};
      const runStatus = String(run?.status || "").toLowerCase();

      if (!checkpointsFetchedRef.current) {
        checkpointsFetchedRef.current = true;
        const routeIdForCp = run?.route_id || runId;
        const cps = await fetchCheckpoints(routeIdForCp);
        checkpointsRef.current = cps;
        setCheckpointCount(cps.length);
      }

      const lastTsRaw =
        latest?.ts || latest?.recorded_at || latest?.created_at || "";
      setMeta({
        title: run?.title || "—",
        from: run?.from_name || "—",
        to: run?.to_name || "—",
        vehicle: run?.vehicle || "—",
        lastTs: lastTsRaw ? formatUtcAsIST(lastTsRaw) : "—",
        distanceKm: fmtKm(run?.distance_m || payload?.distance_m || 0),
        speedKph: String(fmtKph(latest?.speed_mps || run?.speed_mps || 0)),
        duration: humanDuration(run?.start_time, run?.end_time),
        pointsCount: String(
          payload?.total_points || payload?.points?.length || 0
        ),
      });
      setRunStatus(runStatus);

      if (isGoogleMap) {
        if (runStatus === "planned" && !payload?.latest) {
          drawGooglePlanned(run);
        } else {
          drawGoogleTrack(payload, runStatus);
        }
      } else if (runStatus === "planned" && !payload?.latest) {
        drawLeafletPlanned(run);
      } else {
        drawLeafletTrack(payload, runStatus);
      }
    } catch (err) {
      console.error("Live tracking error:", err);
      setStatus({
        label: err.message || "API error",
        className: "bg-red-100 text-red-700",
      });
      toast.error(err.message || "Failed to fetch tracking data");
    }
  }, [
    apiUrl,
    memberId,
    runId,
    isGoogleMap,
    setRunStatus,
    drawLeafletPlanned,
    drawLeafletTrack,
    drawGooglePlanned,
    drawGoogleTrack,
    fetchCheckpoints,
  ]);

  const fitToBounds = useCallback(() => {
    if (isGoogleMap) {
      const map = googleMapRef.current;
      if (!map || !window.google?.maps) return;
      const bounds = new window.google.maps.LatLngBounds();
      let has = false;
      [
        googleLatestMarkerRef,
        googleFromMarkerRef,
        googleToMarkerRef,
        googleStopMarkerRef,
      ].forEach((ref) => {
        const pos = ref.current?.getPosition?.();
        if (pos) {
          bounds.extend(pos);
          has = true;
        }
      });
      googleCheckpointMarkersRef.current.forEach((m) => {
        const pos = m?.getPosition?.();
        if (pos) {
          bounds.extend(pos);
          has = true;
        }
      });
      renderedLatLngsRef.current.forEach(([lat, lng]) => {
        bounds.extend({ lat, lng });
        has = true;
      });
      if (has) map.fitBounds(bounds, 40);
      else toast("No markers to fit", { icon: "📍" });
      return;
    }

    const map = leafletMapRef.current;
    if (!map) return;
    const group = L.featureGroup([]);
    [
      leafletLatestMarkerRef,
      leafletFromMarkerRef,
      leafletToMarkerRef,
      leafletStopMarkerRef,
    ].forEach((ref) => {
      if (ref.current) group.addLayer(ref.current);
    });
    if (leafletTrackLineRef.current) group.addLayer(leafletTrackLineRef.current);
    leafletCheckpointMarkersRef.current.forEach((m) => group.addLayer(m));
    const bounds = group.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
    else toast("No markers to fit", { icon: "📍" });
  }, [isGoogleMap]);

  const recenter = useCallback(() => {
    if (!latestLatLngRef.current) {
      toast("No live position available", { icon: "📍" });
      return;
    }
    const [lat, lng] = latestLatLngRef.current;
    if (isGoogleMap && googleMapRef.current) {
      googleMapRef.current.panTo({ lat, lng });
    } else if (leafletMapRef.current) {
      leafletMapRef.current.panTo([lat, lng], {
        animate: true,
        duration: 0.8,
      });
    }
  }, [isGoogleMap]);

  useEffect(() => {
    followLiveRef.current = followLive;
  }, [followLive]);

  /* ---------- init maps ---------- */

  useEffect(() => {
    if (loading) return undefined;

    let cancelled = false;
    setMapError("");

    if (isGoogleMap) {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      setMapLoading(true);
      (async () => {
        try {
          await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
          if (cancelled || !googleMapBoxRef.current) return;
          const map = new window.google.maps.Map(googleMapBoxRef.current, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            clickableIcons: false,
          });
          googleMapRef.current = map;
          setMapLoading(false);
        } catch (error) {
          console.error("Google Maps init error:", error);
          const message = getGoogleMapsErrorMessage(error);
          setMapError(message);
          setMapLoading(false);
          toast.error(message);
        }
      })();
      return () => {
        cancelled = true;
        clearGoogleLayers();
        googleMapRef.current = null;
      };
    }

    if (googleMapRef.current) {
      clearGoogleLayers();
      googleMapRef.current = null;
    }
    if (!leafletMapBoxRef.current || leafletMapRef.current) return undefined;

    const map = L.map(leafletMapBoxRef.current, {
      zoomControl: true,
    }).setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
    leafletMapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 20,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    L.control.scale({ imperial: false }).addTo(map);

    const legend = L.control({ position: "bottomright" });
    legend.onAdd = () => {
      const div = L.DomUtil.create("div", "escort-legend");
      div.innerHTML = `
        <div><span style="background:#0ea5e9"></span> Live Track</div>
        <div><span style="background:#16a34a"></span> From Fixed</div>
        <div><span style="background:#dc2626"></span> To Fixed</div>
        <div><span style="background:#f97316"></span> Checkpoint</div>
        <div><span style="background:#7c3aed"></span> Stopped Here</div>
        <div><span style="background:#111827"></span> Live Vehicle</div>
      `;
      return div;
    };
    legend.addTo(map);

    const resizeMap = () => map.invalidateSize(true);
    setTimeout(resizeMap, 100);
    setTimeout(resizeMap, 500);
    window.addEventListener("resize", resizeMap);

    return () => {
      window.removeEventListener("resize", resizeMap);
      map.remove();
      leafletMapRef.current = null;
    };
  }, [loading, isGoogleMap, clearGoogleLayers]);

  useEffect(() => {
    if (loading) return undefined;
    if (isGoogleMap && !googleMapRef.current && !mapError) return undefined;
    if (!isGoogleMap && !leafletMapRef.current) return undefined;

    fittedOnceRef.current = false;
    stableToRef.current = null;
    stableFromRef.current = null;
    checkpointsRef.current = [];
    checkpointsFetchedRef.current = false;
    setCheckpointCount(0);

    if (isGoogleMap) clearGoogleLayers();
    else clearLeafletLayers();

    refresh();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(refresh, ESCORT_POLL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [
    loading,
    memberId,
    runId,
    isGoogleMap,
    mapError,
    clearLeafletLayers,
    clearGoogleLayers,
    refresh,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading EBD Patrol live tracking...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="escort"
      activeMenu="escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
        }}
      />

      <style>{`
        .escort-user-icon,
        .escort-pin-icon,
        .escort-checkpoint-icon {
          transform: translate(-50%, -50%);
        }
        .escort-legend {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border: 1px solid var(--card-border) !important;
          padding: 10px 12px;
          border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12);
          font: 12px/1.25 system-ui, sans-serif;
        }
        .escort-legend div {
          margin: 6px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .escort-legend span {
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 3px;
        }
        .leaflet-container {
          width: 100% !important;
          height: 100% !important;
        }
        .escort-map-el {
          width: 100%;
          height: 100%;
          min-height: 520px;
        }
      `}</style>

      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-hidden">
        <div className="flex items-center gap-3 border-b border-[var(--divider)] bg-[var(--card-bg)] px-4 py-3 flex-wrap">
          <div className="flex items-center gap-2 mr-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 64 64"
              width="26"
              height="26"
            >
              <path
                d="M32 4L8 14v18c0 14 10.5 26 24 30 13.5-4 24-16 24-30V14L32 4z"
                fill="#1d4ed8"
              />
            </svg>
            <span
              className="text-base font-extrabold tracking-tight text-[var(--text-primary)]"
              style={{ fontFamily: FONT_UI }}
            >
              EBD Patrol
            </span>
          </div>

          <span className="text-[var(--text-faint)] text-base select-none">
            |
          </span>

          <h3 className="m-0 text-base font-bold text-[var(--text-primary)]">
            Live Tracking
          </h3>

          <div className="px-3 py-1 text-xs font-extrabold rounded-full bg-[var(--surface)] text-[var(--text-primary)] border border-[var(--surface-border)]">
            {isGoogleMap ? "Google Maps" : "OpenStreetMap"}
          </div>

          <div className="text-xs text-[var(--text-muted)]">
            Member:{" "}
            <b className="text-[var(--text-primary)]">{memberId || "—"}</b>
          </div>

          {runId && (
            <div className="text-xs text-[var(--text-muted)]">
              Run: <b className="text-[var(--text-primary)]">#{runId}</b>
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate("/escort_list")}
            className="ml-auto px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Escort List
          </button>

          <div
            className={`rounded-full px-3 py-1 text-xs font-extrabold ${status.className}`}
          >
            {status.label}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-b border-[var(--divider)] bg-[var(--surface)] px-4 py-2">
          <Info label="Title" value={meta.title} />
          <Info label="From" value={meta.from} />
          <Info label="To" value={meta.to} />
          <Info label="Vehicle" value={meta.vehicle} />
          <Info label="Last update" value={meta.lastTs} mono />
          {checkpointCount > 0 && (
            <Info label="Checkpoints" value={String(checkpointCount)} />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--divider)] bg-[var(--card-bg)] px-4 py-2">
          <button
            type="button"
            onClick={() => setFollowLive((v) => !v)}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--accent)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
            style={{
              background: followLive ? skin.gradient : "#2563eb",
              boxShadow: followLive ? "0 4px 18px var(--accent-glow)" : "none",
            }}
          >
            {followLive ? "Follow Live" : "Paused"}
          </button>

          <button
            type="button"
            onClick={fitToBounds}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--accent)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Fit to Track
          </button>

          <button
            type="button"
            onClick={recenter}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--accent)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Recenter
          </button>

          <div className="flex-1" />

          <Info label="Distance" value={`${meta.distanceKm} km`} />
          <Info label="Speed" value={`${meta.speedKph} km/h`} />
          <Info label="Duration" value={meta.duration} />
          <Info label="Points" value={meta.pointsCount} />
        </div>

        <div className="relative h-[calc(100vh-235px)] min-h-[520px] w-full overflow-hidden bg-[var(--surface)]">
          {!isGoogleMap && (
            <div ref={leafletMapBoxRef} className="escort-map-el absolute inset-0" />
          )}
          {isGoogleMap && (
            <div ref={googleMapBoxRef} className="escort-map-el absolute inset-0" />
          )}

          {mapLoading && (
            <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
              <p className="text-sm font-extrabold text-[var(--text-primary)]">
                Loading Google Maps...
              </p>
            </div>
          )}

          {mapError && (
            <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--surface)] p-5">
              <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 text-center shadow-xl">
                <p className="text-sm font-extrabold text-red-700">
                  Map could not be loaded
                </p>
                <p className="mt-2 text-xs font-semibold text-red-600">
                  {mapError}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Info({ label, value, mono = false }) {
  return (
    <div className="text-xs text-[var(--text-muted)]">
      <span className="mr-1">{label}:</span>
      <b
        className={
          mono
            ? "font-mono text-[var(--text-primary)]"
            : "text-[var(--text-primary)]"
        }
      >
        {value || "—"}
      </b>
    </div>
  );
}
